import React, { useMemo, useState } from "react";
import { coins } from "../../constants/constants";
import { formatAmount } from "../../utils/formatTime";
import useUserAsset from "../../hooks/useUserAssets";
import nairaLogo from "../../assets/images/Naira.svg";
import useAuthAxios from "../../utils/baseAxios";
import { useQuery } from "@tanstack/react-query";
import { API } from "../../constants/api";
import CreateNairaWalletModal, {
  NairaWalletModalVariant,
} from "./CreateNairaWalletModal";
import CompleteNairaWalletProfileModal from "./CompleteNairaWalletProfileModal";
import NairaWalletKycBanner from "./NairaWalletKycBanner";
import { NAIRA_WALLET_ENABLED } from "../../constants/constants";

const Assets = ({ setOpenCoin, setSelectedCoinObj }: any) => {
  const [openCreateNairaWallet, setOpenCreateNairaWallet] = useState(false);
  const [openNairaProfileForm, setOpenNairaProfileForm] = useState(false);
  const [nairaModalVariant, setNairaModalVariant] =
    useState<NairaWalletModalVariant>("create");
  const {
    walletBalance: walletBalances,
    walletBalanceInUsdt,
    loading,
  } = useUserAsset();
  const axiosInstance = useAuthAxios();

  const getNairaWalletStatus = async () => {
    const response = await axiosInstance.get(API.getNairaWalletStatus);
    return response.data?.data;
  };

  const { data: nairaWalletStatus, isLoading: isNairaWalletStatusLoading } =
    useQuery({
      queryKey: ["naira-wallet-status"],
      queryFn: getNairaWalletStatus,
      retry: 1,
      enabled: NAIRA_WALLET_ENABLED,
    });

  const hasNairaWallet = Boolean(nairaWalletStatus?.wallet);
  const requiredFields = nairaWalletStatus?.requiredFields ?? [];
  const needsKycLevel2 =
    !hasNairaWallet && nairaWalletStatus && !nairaWalletStatus.eligible;
  const needsProfileFields =
    !hasNairaWallet &&
    nairaWalletStatus?.eligible &&
    Array.isArray(requiredFields) &&
    requiredFields.length > 0;
  const canOpenNairaWalletFlow =
    nairaWalletStatus?.eligible &&
    !hasNairaWallet &&
    (!requiredFields || requiredFields.length === 0);
  const canOpenNairaModal = canOpenNairaWalletFlow || needsProfileFields;

  const showNairaKycBanner =
    NAIRA_WALLET_ENABLED && !isNairaWalletStatusLoading && needsKycLevel2;

  const showNairaInList =
    NAIRA_WALLET_ENABLED &&
    !isNairaWalletStatusLoading &&
    Boolean(nairaWalletStatus) &&
    !needsKycLevel2;

  const displayCoins = useMemo(() => {
    const nonNairaCoins = coins.filter(
      (coin: any) => coin?.abbr?.toUpperCase() !== "NGN"
    );

    if (!showNairaInList) return nonNairaCoins;

    const nairaCoin = {
      id: "ngn-wallet",
      name: "Naira",
      abbr: "NGN",
      logo: nairaLogo,
      isNaira: true,
    };

    return [nairaCoin, ...nonNairaCoins];
  }, [showNairaInList]);

  const handleNairaToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (needsProfileFields) {
      setNairaModalVariant("profile");
      setOpenCreateNairaWallet(true);
      return;
    }
    if (canOpenNairaWalletFlow) {
      setNairaModalVariant("create");
      setOpenCreateNairaWallet(true);
    }
  };

  return (
    <>
    {showNairaKycBanner && <NairaWalletKycBanner />}
    <div
      className={`w-full font-manrope bg-white dark:bg-[#1C2227] rounded-xl p-4 ${
        showNairaKycBanner ? "mt-4" : "mt-8"
      }`}
    >
      <div className="w-full flex justify-between items-center mb-4">
        <h3 className="font-bold text-paragraph text-[15px] dark:text-white">
          Assets
        </h3>
      </div>
      {displayCoins?.map((coin: any, index: any) => {
        const isNaira = coin?.isNaira || coin?.abbr?.toUpperCase() === "NGN";
        const balance = isNaira
          ? walletBalances?.ngn || 0
          : walletBalances?.[coin?.abbr?.toLowerCase()] || 0;

        return (
          <div
            key={index}
            onClick={() => {
              if (isNaira) {
                if (hasNairaWallet) {
                  setSelectedCoinObj(coin);
                  setOpenCoin(true);
                }
                return;
              }
              setSelectedCoinObj(coin);
              setOpenCoin(true);
            }}
            className={`flex justify-between py-5 items-center ${
              isNaira && !hasNairaWallet
                ? "cursor-default"
                : "cursor-pointer"
            } ${
              displayCoins.length === index + 1
                ? ""
                : "border-gray-100 dark:border-gray-800 border-b"
            }`}
          >
            <div className="flex justify-start items-center gap-2">
              <div className="flex justify-center items-center  w-[24px] h-[24px] rounded-full">
                <img
                  src={coin.logo}
                  className="w-full h-full bg-cover"
                  alt=""
                />
              </div>
              <div>
                <h4 className="text-[#13181B] dark:text-gray-200 font-semibold text-[15px] ">
                  {coin.abbr}
                </h4>
                <div className="flex justify-start gap-3 items-center">
                  <h4 className="text-gray-500 dark:text-gray-400  font-medium text-[11px] ">
                    {coin.name}
                  </h4>
                </div>
              </div>
            </div>
            <div>
              <h4 className="text-[#13181B] dark:text-gray-300 text-right font-semibold text-[15px] ">
                {loading || (isNaira && isNairaWalletStatusLoading)
                  ? "---"
                  : isNaira && !hasNairaWallet
                  ? ""
                  : isNaira
                  ? `₦${formatAmount(balance)}`
                  : `${formatAmount(balance)}${coin?.abbr}`}
              </h4>
              {isNaira && !isNairaWalletStatusLoading && !hasNairaWallet && (
                <div
                  className="flex justify-end"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={handleNairaToggleClick}
                    disabled={!canOpenNairaModal}
                    aria-label="Create Naira wallet"
                    className={`flex w-9 h-5 items-center rounded-full transition-all duration-500 bg-gray-200 dark:bg-gray-500/70 ${
                      canOpenNairaModal
                        ? "cursor-pointer"
                        : "cursor-not-allowed opacity-60"
                    }`}
                  >
                    <span className="h-4 w-4 ml-0.5 rounded-full transition-all duration-500 bg-white dark:bg-gray-100 shadow-sm"></span>
                  </button>
                </div>
              )}
              {!isNaira && (
                <h4 className="text-gray-500 dark:text-gray-400 text-right  font-medium text-[11px] ">
                  ${" "}
                  {loading
                    ? "---"
                    : formatAmount(
                        walletBalanceInUsdt?.[coin?.abbr?.toLowerCase()] || 0
                      )}
                </h4>
              )}
            </div>{" "}
          </div>
        );
      })}
    </div>
    {openCreateNairaWallet && (
      <CreateNairaWalletModal
        variant={nairaModalVariant}
        onClose={() => setOpenCreateNairaWallet(false)}
        onCompleteProfile={() => {
          setOpenCreateNairaWallet(false);
          setOpenNairaProfileForm(true);
        }}
      />
    )}
    {openNairaProfileForm && (
      <CompleteNairaWalletProfileModal
        requiredFields={requiredFields}
        onClose={() => setOpenNairaProfileForm(false)}
        onSuccess={() => {
          setOpenCreateNairaWallet(false);
          setOpenNairaProfileForm(false);
        }}
      />
    )}
    </>
  );
};

export default Assets;
